import classNames from "classnames/dedupe";
import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  BundlesButton,
  Carousel,
  Column,
  Divider,
  Drawer,
  FullscreenImage,
  GalleryContext,
  HeartButton,
  Image,
  ImageContentWidget,
  ImageZoom,
  Link,
  LinkButton,
  Modal,
  PayButton,
  ProductBrand,
  ProductGallery,
  ProductHighlights,
  ProductPrice,
  ProductReviews,
  ProductTechnicalDetails,
  Row,
  SvgIcon,
  Typography,
  useGallery,
  YoutubeEmbed,
  ICarousel,
} from "../../components";
import BrandLogo from "../../components/PDP/BrandLogo/BrandLogo";
import ProductColors from "../../components/PDP/ProductColors/ProductColors";
import ProductVideo from "../../components/PDP/ProductVideo/ProductVideo";
import SizeSelectorTiles from "../../components/ActionsSelectors/SizeSelector/SizeSelectorTiles";
import { forceRerenderCssAnimation } from "../../helpers";
import useCSSUnits from "../../hooks/useCSSUnits";
import useScrollDir from "../../hooks/useScrollDir";

import {
  AddToCartIcon,
  CheckmarkIcon,
  ClubLogoIcon,
  LocationPinIcon,
  ThreeSixtyView,
  USPCallIcon,
  USPClockIcon,
  USPTruckIcon,
  VideoPlayIcon,
  VideoPlayLargeIcon,
} from "../../icons";

import { PDPSizesFilterData } from "../../mock/filtersMock";
import { DEMO_VIDEO_URL, mockProduct } from "../../mock/product";
import { mockedProductBrand } from "../../mock/productBrand";

import {
  mockedGalleryItems,
  mockedProductHighlights,
  mockedProductStoryImages,
  productTechnicalDetails,
} from "../../mock/productPageData";

import { Footer } from "../Footer";
import { DesktopMenu } from "../Menu";

const MENU_HEIGHT = 52;

export interface IPDPPageProps {
  /**
   * builds a page with alternative view
   */
  alternativePDPLayout?: boolean;
}

const PDP: FC<IPDPPageProps> = ({ alternativePDPLayout = false }) => {
  /** Global script. Has to be at root / DOMReady */
  useCSSUnits();

  const [images, setImages] = useState(() => mockedGalleryItems.slice());
  const fullScreenImages = images.filter((item) => !item.video);

  const dir = useScrollDir();

  const [fullScreen, setFullScreen] = useState(false);
  const [fullScreenVideo, setFullScreenVideo] = useState(false);
  const [fullScreen360, setFullScreen360] = useState(false);
  const [mainImageHeight, setMainImageHeight] = useState<number>();
  const [isVideoSlideShown, setIsVideoSlideShown] = useState<boolean>(false);

  const [selectedImage, handleOnSelect] = useGallery(
    mockedGalleryItems,
    mockedGalleryItems[0],
  );

  const [primaryInstance, setPrimaryInstance] =
    useState<ICarousel["params"]["mainCarouselInstance"]>(null);

  const selectedImageIndex = useMemo(
    () =>
      selectedImage
        ? images.findIndex((item) => item.id === selectedImage.id)
        : 0,
    [selectedImage, images],
  );

  useEffect(() => {
    if (!primaryInstance) return;
    primaryInstance.slideTo(selectedImageIndex);
  }, [selectedImageIndex, primaryInstance]);

  useEffect(() => {
    const videoItemIndex = images.findIndex((item) => item.video);
    videoItemIndex === selectedImageIndex &&
      !fullScreen360 &&
      setIsVideoSlideShown(true);
    return () => setIsVideoSlideShown(false);
  }, [selectedImageIndex, images, fullScreen360]);

  const handleColorSelection = useCallback(() => {
    /** JUST FOR TESTING PURPOSES ! REMOVE IT IN PRODUCTION ! */
    const newImages = images.slice().reverse();
    setImages(newImages);
    handleOnSelect(newImages[0]); // set first
    /** Add some fetching to receive new images (for different color params)
     *  here */
  }, [handleOnSelect, images]);

  const showVideo = false;
  const videoUrl = DEMO_VIDEO_URL;
  const show360 = true;

  const openFullScreen = useCallback(() => setFullScreen(true), []);
  const closeFullScreen = useCallback(() => setFullScreen(false), []);

  const showFullScreenVideo = useCallback(() => setFullScreenVideo(true), []);
  const hideFullScreenVideo = useCallback(
    () => setIsVideoSlideShown(false),
    [],
  );

  const showFullScreen360 = useCallback(() => setFullScreen360(true), []);
  const hideFullScreen360 = useCallback(() => setFullScreen360(false), []);

  const mobileSlides = useMemo(() => {
    const slides = images
      .filter((item) => !item.video)
      .map((item) => (
        <div
          key={item.id}
          className="pdp-main--image--list--item swiper-slide"
          onClick={openFullScreen}
        >
          <Image src={item.src} alt={item.alt} />
        </div>
      ));

    if (showVideo) {
      slides.push(
        <Fragment key={-1}>
          <div className="pdp-main-image" onClick={showFullScreenVideo}>
            <Image src="assets/images/pdp-module7.png" />
            <Button
              classes={{
                root: "position-absolute top-50 start-50 translate-middle btn-animated-fade",
              }}
              variant="icon"
            >
              <VideoPlayLargeIcon />
            </Button>
          </div>
          <ProductVideo
            url={videoUrl}
            isOpen={fullScreenVideo}
            onClose={hideFullScreenVideo}
          />
        </Fragment>,
      );
    }

    if (show360) {
      slides.push(
        <Fragment key={-2}>
          <div className="pdp-main-image" onClick={showFullScreen360}>
            <Image src="assets/images/pdp-module7.png" />
            <Button
              classes={{
                root: "position-absolute top-50 start-50 translate-middle btn-animated-fade",
              }}
              variant="icon"
            >
              <SvgIcon
                icon={ThreeSixtyView}
                classes={{ root: "icon-container--medium" }}
              />
            </Button>
          </div>
          <Modal isOpen={fullScreen360} onClose={hideFullScreen360}>
            <div className="m-10 text-center">Content for 360</div>
          </Modal>
        </Fragment>,
      );
    }

    return slides;
  }, [
    fullScreen360,
    fullScreenVideo,
    hideFullScreen360,
    hideFullScreenVideo,
    images,
    openFullScreen,
    show360,
    showFullScreen360,
    showFullScreenVideo,
    showVideo,
    videoUrl,
  ]);

  const desktopSlides = useMemo(() => {
    const slides = images.map((item) =>
      item.video ? (
        <YoutubeEmbed
          key={item.id}
          embedId={item.video.embedId}
          classes={{ root: "youtube-video-container" }}
          stopVideo={!isVideoSlideShown}
        />
      ) : (
        <ImageZoom
          key={item.id}
          img={selectedImage.src}
          labels={{ helper: "CLICK TO VIEW FULL SCREEN" }}
          badges={[
            {
              text: "-30%",
              color: "red",
            },
            {
              text: "Best Choice",
              color: "blue",
            },
          ]}
          onClick={openFullScreen}
          getMainImageHeight={setMainImageHeight}
        />
      ),
    );

    return slides;
  }, [openFullScreen, selectedImage, images, isVideoSlideShown]);

  /** Purchase / add to cart button animation */
  const [animatingPurchase, setAnimatingPurchase] = useState(0);
  const [tm, setTm] = useState<number>();
  const endListener = useCallback(
    (node: HTMLElement, done: () => void) =>
      node.addEventListener("transitionend", done, false),
    [],
  );
  const handleAddToCart = useCallback(() => {
    const ANIM_DELAY = 750;
    if (tm) {
      clearTimeout(tm);
      setTm(null);
      setAnimatingPurchase(animatingPurchase + 1);
    } else {
      setAnimatingPurchase(1);
    }
    const newTm = setTimeout(() => {
      setAnimatingPurchase(0);
    }, ANIM_DELAY);
    setTm(newTm as unknown as number);
  }, [animatingPurchase, tm]);

  /** Add to favourite + animation */
  const [favAnim, setFavAnim] = useState(false);
  const favRef = useRef(null);
  const [isFavourite, setIsFavourite] = useState(false);
  const handleFavAnimEnd = useCallback(() => setFavAnim(false), []);
  const handleFavAnim = useCallback(() => {
    setFavAnim(false);
    if (favRef?.current) {
      forceRerenderCssAnimation(favRef.current);
    }
    setFavAnim(true);
  }, []);
  const handleFavourite = useCallback(() => {
    setIsFavourite(!isFavourite);
    handleFavAnim();
  }, [handleFavAnim, isFavourite]);

  const galleryContext = useMemo(
    () => ({
      images: mockedGalleryItems,
      activeItem: selectedImage,
      onSelect: handleOnSelect,
    }),
    [handleOnSelect, selectedImage],
  );

  const [grindingDrawerOpen, setGrindingDrawerOpen] = useState<boolean>(false);
  const grindingButtonClickHandler = () => {
    setGrindingDrawerOpen(!grindingDrawerOpen);
  };

  const productPriceClasses = classNames("product-values", {
    "mt-6 mb-4": alternativePDPLayout,
  });

  const productGalleryItem = classNames("product-gallery--item", {
    "w-60px h-60px me-0": alternativePDPLayout,
  });

  const productPrice = (
    <div className={productPriceClasses}>
      <ProductPrice
        prices={[
          {
            currency: "€",
            price: "85.5",
          },
          {
            currency: "€",
            price: "135",
            variant: "original",
          },
        ]}
        classes={{ root: "mb-0" }}
      />
      <div className="product-loyalty">
        <SvgIcon
          icon={ClubLogoIcon}
          width={42}
          height={16}
          classes={{ root: "club-logo" }}
          style={{ marginTop: "-2px" }}
        />
        <LinkButton variant="bold" classes={{ root: "ms-4" }}>
          85 points
        </LinkButton>
      </div>
    </div>
  );

  const productInfo = (
    <>
      <ProductBrand
        useHeading="h1"
        {...mockedProductBrand}
        iconData={{
          type: "svg-icon",
          data: {
            icon: "NikeIcon",
          },
        }}
      />

      {alternativePDPLayout && productPrice}

      {/* Product description. Show <Typography> "as" props */}
      <Typography
        classes={{
          root: classNames("pdp-main product-description d-none d-lgm-block", {
            "me-0": alternativePDPLayout,
          }),
        }}
        as="p"
      >
        Take on new adventures whatever the weather and terrain with the Leonis
        GTX. It has a fully waterproof Gore-Tex membrane so that your feet stay
        dry. In addition, it features an optimized Contagrip outsole and
        Sensifit technology for a more precise and better fit.
      </Typography>
    </>
  );

  const videoGalleryDesktopItem = showVideo ? (
    <Fragment key={-1}>
      <div className={productGalleryItem} onClick={showFullScreenVideo}>
        <SvgIcon icon={VideoPlayIcon} />
      </div>
      <ProductVideo
        url={videoUrl}
        isOpen={fullScreenVideo}
        onClose={hideFullScreenVideo}
      />
    </Fragment>
  ) : undefined;

  const roundViewGalleryDesktopItem = show360 ? (
    <Fragment key={-2}>
      <div className={productGalleryItem} onClick={showFullScreen360}>
        <SvgIcon
          icon={ThreeSixtyView}
          classes={{ root: "icon-container--medium" }}
        />
      </div>
      <Modal isOpen={fullScreen360} onClose={hideFullScreen360}>
        <div className="m-10 text-center">Content for 360</div>
      </Modal>
    </Fragment>
  ) : undefined;

  const isShowProductGallery = images.length > 1 || showVideo || show360;

  return (
    <>
      <DesktopMenu />
      <div className="h-100 d-block bg-red" />

      <div className="container-fluid flex-grow-1 position-relative mb-10">
        {/* Breadcrumb */}
        <Breadcrumb
          backLink={{ text: "back", href: "/" }}
          mobileBackLink={{ text: "shoes", href: "/" }}
        >
          <BreadcrumbItem text="Home" href="/" />
          <BreadcrumbItem text="Men" />
          <BreadcrumbItem text="Shoes" />
          <BreadcrumbItem active text="Last no link" />
        </Breadcrumb>
        <Row classes={{ root: "align-items-start position-relative" }}>
          {/* Sidebar */}
          <Column
            lg={4}
            classes={{ root: "order-lg-last ps-xl-10 pdp-sidebar" }}
            style={{ top: `${16 + (dir === "down" ? 0 : MENU_HEIGHT)}px` }}
          >
            {/* Mobile content */}
            <div className="d-lgm-none">
              <div className="d-lg-none pdp-carousel-wrapper">
                <Carousel
                  variant="product-preview-carousel"
                  classes={{ root: "pdp-carousel" }}
                >
                  {mobileSlides}
                </Carousel>
                <div className="swiper-scrollbar" />
                <div className="product-badges">
                  <Badge text="-30%" color="red" />
                  <Badge text="Best Choice" color="blue" />
                </div>
              </div>
              {/* Mobile product intro */}
              <BrandLogo
                iconData={{
                  type: "svg-icon",
                  data: {
                    icon: "NikeIcon",
                  },
                }}
              />
              <div className="product-intro my-5">
                <div className="pdp-main product-title">
                  <Link href="https://intersport.com">Link</Link>
                  <Link classes={{ root: "product-model" }} href="/">
                    SolarGlide 3 Schoenen
                  </Link>
                </div>
              </div>
              {/* Mobile product info */}
              <div className="pdp-main review-section mb-5">
                <ProductReviews reviews="15" rating="4.5" />
              </div>
            </div>
            {/* End mobile content */}

            {/* Product info for alternative layout */}
            {alternativePDPLayout && productInfo}

            {/* Product prices and loyalty */}
            {!alternativePDPLayout && productPrice}
            {/* Product color picker */}
            <ProductColors
              classes={{
                carouselWrapper: "pdp-sidebar product-card__preview-carousel",
                carousel: "d-none d-lg-block",
              }}
              title="Colour"
              data={mockProduct.colors}
              onSelect={handleColorSelection}
              activeColorId="3"
            />

            {/* Size selector */}
            <SizeSelectorTiles
              filterData={PDPSizesFilterData}
              variant="light"
              sizeSelectorLabels={{
                findMySize: "Find my size",
                sizeChart: "Size Chart",
              }}
            />
            <LinkButton
              variant="icon"
              startIcon={LocationPinIcon}
              classes={{ root: "mb-8" }}
            >
              Check availability in the store
            </LinkButton>
            <BundlesButton
              buttonText="No Grinding Added"
              title="Grinding service:"
              ariaLabel="click to see available grinding services"
              onClick={grindingButtonClickHandler}
            />
            <Drawer
              opened={grindingDrawerOpen}
              onClose={() => {
                setGrindingDrawerOpen(false);
              }}
              header="add grinding service"
            />
            {/* Action buttons */}
            <div className="d-flex flex-column mb-8">
              <div className="pdp-fixed-buttons">
                <Button
                  variant="conversion"
                  classes={{
                    root: classNames(
                      "flex-grow-1 me-6 position-relative overflow-hidden",
                      { "box-shadow-none": !!animatingPurchase },
                    ),
                  }}
                  onClick={handleAddToCart}
                >
                  <div
                    className={classNames("uds-btn-fade-in-left", {
                      "uds-btn-fade-in-left--active": animatingPurchase,
                    })}
                  />
                  <SwitchTransition>
                    <CSSTransition
                      key={String(animatingPurchase)}
                      classNames="uds-appear-smooth"
                      addEndListener={endListener as any}
                      timeout={300}
                      mountOnEnter
                      unmountOnExit
                    >
                      {animatingPurchase ? (
                        <div className="uds-btn-fade-in-left__content d-flex align-items-baseline">
                          <CheckmarkIcon
                            className="me-4"
                            width="24"
                            height="10"
                          />
                          Added to cart
                        </div>
                      ) : (
                        <div className="uds-btn-fade-in-left__content uds-btn-fade-in-left__content--alt d-flex align-items-center">
                          <SvgIcon
                            icon={AddToCartIcon}
                            classes={{ root: "me-4" }}
                          />
                          Add To Cart
                        </div>
                      )}
                    </CSSTransition>
                  </SwitchTransition>
                </Button>
                <HeartButton
                  active={isFavourite}
                  isAnimating={favAnim}
                  onAnimationEnd={handleFavAnimEnd}
                  onClick={handleFavourite}
                />
              </div>
              <PayButton
                variant="paypal-pay"
                text="Buy With"
                classes={{ root: "mb-5" }}
              />

              <Typography
                as="p"
                classes={{ root: "text-desc text-color-grey-500 mb-0" }}
              >
                Google Pay, Apple Pay, debit or credit card are also supported
              </Typography>
            </div>

            {/* Delivery info */}
            <div className="border__dotted mt-8 mb-7">
              <ul className="d-flex flex-column p-6 text-nav m-0">
                <li className="d-flex mb-4">
                  <SvgIcon
                    icon={USPTruckIcon}
                    width={24}
                    height={16}
                    isIcon={false}
                  />
                  Delivered by&nbsp;<span>tomorrow</span>
                </li>
                <li className="d-flex mb-4">
                  <SvgIcon
                    icon={USPClockIcon}
                    width={24}
                    height={16}
                    isIcon={false}
                  />
                  Available in store&nbsp;
                  <span> 8:00-18:00</span>
                </li>
                <li className="d-flex mb-4">
                  <SvgIcon
                    icon={USPCallIcon}
                    width={24}
                    height={16}
                    isIcon={false}
                  />
                  Call us:&nbsp;<span>+31612214182</span>
                </li>
              </ul>
            </div>
          </Column>

          {/* Product content */}
          <Column lg={8}>
            <GalleryContext.Provider value={galleryContext}>
              <div
                className={`d-flex flex-lgm-row flex-nowrap pe-xl-0 ${
                  alternativePDPLayout ? "flex-row" : "flex-column"
                }`}
              >
                {/* Desktop Product Content */}
                <div
                  className={`d-flex flex-column ${
                    alternativePDPLayout ? "order-first" : "order-last"
                  } order-lgm-first`}
                >
                  {!alternativePDPLayout && (
                    <>
                      {/* Product Info */}
                      {productInfo}
                      <LinkButton
                        variant="bold"
                        classes={{ root: "mb-4 me-auto d-none d-lgm-block" }}
                      >
                        Overview
                      </LinkButton>
                      <LinkButton
                        variant="bold"
                        classes={{ root: "mb-4 me-auto d-none d-lgm-block" }}
                      >
                        Technical Details
                      </LinkButton>
                    </>
                  )}

                  {/* Desktop gallery and main image */}
                  {isShowProductGallery && (
                    <ProductGallery
                      classes={{
                        root: `product-gallery d-none d-lg-flex ${
                          alternativePDPLayout ? "me-6" : "me-3"
                        }`,
                      }}
                      images={images}
                      activeItem={selectedImage}
                      isCarousel={alternativePDPLayout}
                      carouselHeight={mainImageHeight}
                      videoGalleryItem={videoGalleryDesktopItem}
                      roundViewGalleryItem={roundViewGalleryDesktopItem}
                    />
                  )}
                </div>
                <Carousel
                  variant="pdp-main-carousel"
                  onInstance={setPrimaryInstance}
                  classes={{ root: "d-none d-lg-block flex-grow-1" }}
                >
                  {desktopSlides}
                </Carousel>

                <FullscreenImage
                  activeItem={selectedImage}
                  images={fullScreenImages}
                  isOpen={fullScreen}
                  onSelect={handleOnSelect}
                  onClose={closeFullScreen}
                />
              </div>

              <Divider classes={{ root: "my-10" }} size="bold" />

              {/* Highlights */}
              <Typography
                variant="section-title"
                classes={{ root: "mb-7 mb-md-8" }}
                color="white"
              >
                Overview
              </Typography>
              <ProductHighlights highlights={mockedProductHighlights} />

              {/* Technical details */}
              <Typography
                variant="section-title"
                classes={{ root: "mb-7 mb-md-8 mt-8" }}
                color="white"
              >
                Technical Details
              </Typography>
              <ProductTechnicalDetails items={productTechnicalDetails} />

              {/* Product Story */}
              <Typography
                variant="section-title"
                classes={{ root: "mt-10 mb-7 mb-md-8" }}
                color="white"
              >
                Product Story
              </Typography>
              <Typography
                variant="mobile-header-20-bold"
                as="h3"
                classes={{ root: "mb-6" }}
              >
                Unlock Your Skills
              </Typography>
              <Typography
                variant="text-paragraph"
                as="p"
                classes={{
                  root: "mb-8",
                }}
              >
                Take control of your game with the new Phantom. Its data-driven
                design and grippy texture are engineered for precise attacks so
                you can steal back your win. Featuring Nike&apos;s Flyknit top
                and specialised traction so you can beat your opponent with
                pinpoint delivery and keep your teammates smiling with assist
                after assist.
              </Typography>

              {/* Image Widget */}
              <ImageContentWidget images={mockedProductStoryImages} />
              <Typography
                variant="mobile-header-20-bold"
                as="h3"
                classes={{ root: "mb-6 mt-8" }}
              >
                Play Free
              </Typography>
              <Typography
                variant="text-paragraph"
                as="p"
                classes={{
                  root: "mb-8",
                }}
              >
                Take control of your game with the new Phantom. Its data-driven
                design and grippy texture are engineered for precise attacks so
                you can steal back your win. Featuring Nike&apos;s Flyknit top
                and specialised traction so you can beat your opponent with
                pinpoint delivery and keep your teammates smiling with assist
                after assist.
              </Typography>
            </GalleryContext.Provider>
          </Column>
        </Row>
      </div>

      {/* Footers */}
      <div className="container-fluid">
        <Footer />
      </div>
    </>
  );
};

export default PDP;
